import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getUser, updateUser, updateUserDisc, resendInvitation, sendPasswordReset, setUserPassword, deleteUser, uploadDiscPdf, getDiscPdfUrl, deleteDiscPdf } from '../lib/api';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Slider } from '../components/ui/slider';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  ArrowLeft, 
  Save, 
  Mail, 
  Loader2, 
  CheckCircle2,
  AlertCircle,
  Upload,
  FileText,
  Download,
  Trash2,
  KeyRound,
  UserX,
  Lock
} from 'lucide-react';

const AdminUserDetailPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingDisc, setSavingDisc] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [sendingReset, setSendingReset] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [settingPassword, setSettingPassword] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState('');
  
  // Edit form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'consultant',
    is_active: true
  });
  
  // DISC form state
  const [discData, setDiscData] = useState({
    D: 50,
    I: 50,
    S: 50,
    C: 50,
    notes: ''
  });

  useEffect(() => {
    loadUser();
  }, [userId]);

  const loadUser = async () => {
    setLoading(true);
    try {
      const response = await getUser(userId);
      setUser(response.data);
      setFormData({
        name: response.data.name || '',
        email: response.data.email || '',
        phone: response.data.phone || '',
        role: response.data.role || 'consultant',
        is_active: response.data.is_active,
        sms_enabled: response.data.sms_enabled !== false
      });
      if (response.data.disc_profile_json) {
        setDiscData({
          D: response.data.disc_profile_json.D || 50,
          I: response.data.disc_profile_json.I || 50,
          S: response.data.disc_profile_json.S || 50,
          C: response.data.disc_profile_json.C || 50,
          notes: response.data.disc_profile_json.notes || ''
        });
      }
    } catch (err) {
      setError('Kunne ikke hente bruger');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveUser = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    
    try {
      await updateUser(userId, formData);
      setSuccessMessage('Bruger opdateret');
      setTimeout(() => setSuccessMessage(''), 3000);
      loadUser();
    } catch (err) {
      setError(err.response?.data?.detail || 'Kunne ikke opdatere bruger');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDisc = async (e) => {
    e.preventDefault();
    setError('');
    setSavingDisc(true);
    
    try {
      await updateUserDisc(userId, discData);
      setSuccessMessage('DISC profil opdateret');
      setTimeout(() => setSuccessMessage(''), 3000);
      loadUser();
    } catch (err) {
      setError(err.response?.data?.detail || 'Kunne ikke opdatere DISC');
    } finally {
      setSavingDisc(false);
    }
  };

  const handlePdfUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setError('Kun PDF-filer er tilladt');
      return;
    }
    
    setUploadingPdf(true);
    setError('');
    
    try {
      await uploadDiscPdf(userId, file);
      setSuccessMessage('DISC PDF uploaded');
      setTimeout(() => setSuccessMessage(''), 3000);
      loadUser();
    } catch (err) {
      setError(err.response?.data?.detail || 'Kunne ikke uploade PDF');
    } finally {
      setUploadingPdf(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDownloadPdf = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(getDiscPdfUrl(userId), {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', user?.disc_pdf_filename || 'disc-profile.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Kunne ikke downloade PDF');
    }
  };

  const handleDeletePdf = async () => {
    if (!window.confirm('Er du sikker på du vil slette DISC PDF?')) return;
    
    try {
      await deleteDiscPdf(userId);
      setSuccessMessage('DISC PDF slettet');
      setTimeout(() => setSuccessMessage(''), 3000);
      loadUser();
    } catch (err) {
      setError(err.response?.data?.detail || 'Kunne ikke slette PDF');
    }
  };

  const handleResendInvite = async () => {
    try {
      await resendInvitation(userId);
      setSuccessMessage('Invitation sendt');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError('Kunne ikke sende invitation');
    }
  };

  const handlePasswordReset = async () => {
    setSendingReset(true);
    setError('');
    try {
      await sendPasswordReset(userId);
      setSuccessMessage('Password reset email sendt');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Kunne ikke sende password reset');
    } finally {
      setSendingReset(false);
    }
  };

  const handleDeleteUser = async () => {
    const confirmText = `Er du sikker på du vil PERMANENT slette ${user?.name}?\n\nDette kan ikke fortrydes.`;
    if (!window.confirm(confirmText)) return;
    
    setDeleting(true);
    setError('');
    try {
      await deleteUser(userId);
      navigate('/admin/users');
    } catch (err) {
      setError(err.response?.data?.detail || 'Kunne ikke slette medarbejder');
      setDeleting(false);
    }
  };

  const handleSetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      setError('Password skal være mindst 6 tegn');
      return;
    }
    
    setSettingPassword(true);
    setError('');
    try {
      await setUserPassword(userId, newPassword);
      setNewPassword('');
      setSuccessMessage('Password oprettet');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Kunne ikke oprette password');
    } finally {
      setSettingPassword(false);
    }
  };

  // Calculate primary/secondary DISC
  const calculateDiscPrimary = () => {
    const scores = [
      { letter: 'D', value: discData.D },
      { letter: 'I', value: discData.I },
      { letter: 'S', value: discData.S },
      { letter: 'C', value: discData.C }
    ];
    scores.sort((a, b) => b.value - a.value);
    return {
      primary: scores[0].letter,
      secondary: scores[1].value > 0 ? scores[1].letter : null
    };
  };

  const discResult = calculateDiscPrimary();

  if (loading) {
    return (
      <div className="max-w-2xl">
        <Skeleton className="h-8 w-48 mb-8" />
        <Skeleton className="h-64 w-full mb-6" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="max-w-2xl">
        <p className="text-slate-500">Bruger ikke fundet</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <Button variant="ghost" size="icon" onClick={() => navigate('/admin/users')} data-testid="back-btn">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-outfit">{user.name}</h1>
          <p className="text-slate-500">{user.email}</p>
        </div>
      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <div className="flex items-center gap-2 p-4 bg-emerald-50 border border-emerald-100 rounded-lg text-emerald-700 mb-6">
          <CheckCircle2 className="h-5 w-5" />
          <span>{successMessage}</span>
        </div>
      )}
      
      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-100 rounded-lg text-red-600 mb-6">
          <AlertCircle className="h-5 w-5" />
          <span>{error}</span>
        </div>
      )}

      {/* User Info Card */}
      <Card className="border-slate-100 mb-6">
        <CardHeader>
          <CardTitle className="text-lg font-outfit">Brugeroplysninger</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSaveUser} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Navn</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  data-testid="edit-name"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  data-testid="edit-email"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone">Telefon</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  data-testid="edit-phone"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="role">Rolle</Label>
                <Select 
                  value={formData.role} 
                  onValueChange={(value) => setFormData({...formData, role: value})}
                >
                  <SelectTrigger data-testid="edit-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="consultant">Konsulent</SelectItem>
                    <SelectItem value="installer">Montør</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="flex items-center justify-between pt-2">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Switch
                    id="is_active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({...formData, is_active: checked})}
                    data-testid="edit-active"
                  />
                  <Label htmlFor="is_active">Aktiv</Label>
                </div>
              </div>
              <p className="text-xs text-slate-500">
                Aktive konsulenter modtager SMS og deres kunder modtager surveys
              </p>
            </div>
            
            <div className="flex gap-2 pt-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={handleResendInvite}
                data-testid="resend-invite-btn"
              >
                <Mail className="h-4 w-4 mr-2" />
                Send invitation
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={handlePasswordReset}
                disabled={sendingReset}
                data-testid="reset-password-btn"
              >
                {sendingReset ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <KeyRound className="h-4 w-4 mr-2" />
                )}
                Nulstil password
              </Button>
            </div>
            
            {/* Set Password Directly */}
            <div className="pt-4 border-t border-slate-100">
              <Label className="text-slate-700 mb-2 block">Opret login (sæt password direkte)</Label>
              <div className="flex gap-2">
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Nyt password (min. 6 tegn)"
                  className="flex-1"
                  data-testid="set-password-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleSetPassword}
                  disabled={settingPassword || !newPassword}
                  data-testid="set-password-btn"
                >
                  {settingPassword ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Lock className="h-4 w-4 mr-2" />
                  )}
                  Opret login
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Brug dette til at oprette login uden at sende email
              </p>
            </div>
            
            <div className="flex justify-end pt-4">
              <Button 
                type="submit" 
                className="bg-slate-900 hover:bg-slate-800"
                disabled={saving}
                data-testid="save-user-btn"
              >
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Gem ændringer
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* DISC Profile Card */}
      <Card className="border-slate-100 mb-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg font-outfit">DISC Profil</CardTitle>
              <CardDescription>Indstil konsulentens DISC-værdier (0-100)</CardDescription>
            </div>
            {discResult.primary && (
              <Badge className="bg-teal-50 text-teal-700 border-teal-100 text-lg px-3 py-1">
                {discResult.primary}{discResult.secondary ? `/${discResult.secondary}` : ''}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSaveDisc} className="space-y-6">
            {/* D Slider */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base font-medium">
                  <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-red-100 text-red-700 font-bold mr-2">D</span>
                  Dominans
                </Label>
                <span className="text-lg font-semibold text-slate-900">{discData.D}</span>
              </div>
              <Slider
                value={[discData.D]}
                onValueChange={([value]) => setDiscData({...discData, D: value})}
                max={100}
                step={1}
                className="[&_[role=slider]]:bg-red-500"
                data-testid="disc-slider-d"
              />
            </div>

            {/* I Slider */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base font-medium">
                  <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-yellow-100 text-yellow-700 font-bold mr-2">I</span>
                  Indflydelse
                </Label>
                <span className="text-lg font-semibold text-slate-900">{discData.I}</span>
              </div>
              <Slider
                value={[discData.I]}
                onValueChange={([value]) => setDiscData({...discData, I: value})}
                max={100}
                step={1}
                className="[&_[role=slider]]:bg-yellow-500"
                data-testid="disc-slider-i"
              />
            </div>

            {/* S Slider */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base font-medium">
                  <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-green-100 text-green-700 font-bold mr-2">S</span>
                  Stabilitet
                </Label>
                <span className="text-lg font-semibold text-slate-900">{discData.S}</span>
              </div>
              <Slider
                value={[discData.S]}
                onValueChange={([value]) => setDiscData({...discData, S: value})}
                max={100}
                step={1}
                className="[&_[role=slider]]:bg-green-500"
                data-testid="disc-slider-s"
              />
            </div>

            {/* C Slider */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base font-medium">
                  <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-blue-100 text-blue-700 font-bold mr-2">C</span>
                  Compliance
                </Label>
                <span className="text-lg font-semibold text-slate-900">{discData.C}</span>
              </div>
              <Slider
                value={[discData.C]}
                onValueChange={([value]) => setDiscData({...discData, C: value})}
                max={100}
                step={1}
                className="[&_[role=slider]]:bg-blue-500"
                data-testid="disc-slider-c"
              />
            </div>

            {/* Notes */}
            <div className="space-y-2 pt-4">
              <Label htmlFor="disc-notes">Noter (valgfri)</Label>
              <Textarea
                id="disc-notes"
                value={discData.notes}
                onChange={(e) => setDiscData({...discData, notes: e.target.value})}
                placeholder="Evt. noter om profilen..."
                rows={3}
                data-testid="disc-notes"
              />
            </div>

            <div className="flex justify-end pt-4">
              <Button 
                type="submit" 
                className="bg-slate-900 hover:bg-slate-800"
                disabled={savingDisc}
                data-testid="save-disc-btn"
              >
                {savingDisc ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Gem DISC
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* DISC PDF Upload Card */}
      <Card className="border-slate-100">
        <CardHeader>
          <CardTitle className="text-lg font-outfit flex items-center gap-2">
            <FileText className="h-5 w-5 text-teal-600" />
            DISC Test PDF
          </CardTitle>
          <CardDescription>Upload den originale DISC-test som PDF</CardDescription>
        </CardHeader>
        <CardContent>
          {user?.disc_pdf_uploaded ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-teal-50 rounded-lg border border-teal-100">
                <div className="flex items-center gap-3">
                  <FileText className="h-8 w-8 text-teal-600" />
                  <div>
                    <p className="font-medium text-slate-900">{user.disc_pdf_filename}</p>
                    <p className="text-sm text-slate-500">PDF uploaded</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDownloadPdf}
                    data-testid="download-pdf-btn"
                  >
                    <Download className="h-4 w-4 mr-1" />
                    Download
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDeletePdf}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    data-testid="delete-pdf-btn"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              <div className="text-center">
                <p className="text-sm text-slate-500 mb-2">Eller upload en ny fil</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf"
                  onChange={handlePdfUpload}
                  className="hidden"
                  id="pdf-upload-replace"
                />
                <Button
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingPdf}
                  data-testid="replace-pdf-btn"
                >
                  {uploadingPdf ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                  Erstat PDF
                </Button>
              </div>
            </div>
          ) : (
            <div className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center">
              <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 mb-4">Ingen DISC PDF uploaded endnu</p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf"
                onChange={handlePdfUpload}
                className="hidden"
                id="pdf-upload"
              />
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingPdf}
                data-testid="upload-pdf-btn"
              >
                {uploadingPdf ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                Upload DISC PDF
              </Button>
              <p className="text-xs text-slate-400 mt-3">Accepterer PDF-filer op til 10MB</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete User Card */}
      <Card className="border-red-100 mt-6">
        <CardHeader>
          <CardTitle className="text-lg font-outfit text-red-600 flex items-center gap-2">
            <UserX className="h-5 w-5" />
            Farezone
          </CardTitle>
          <CardDescription>Permanent sletning af medarbejder</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg border border-red-100">
            <div>
              <p className="font-medium text-slate-900">Slet medarbejder permanent</p>
              <p className="text-sm text-slate-500">Denne handling kan ikke fortrydes. Alle data fjernes.</p>
            </div>
            <Button
              variant="destructive"
              onClick={handleDeleteUser}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="delete-user-btn"
            >
              {deleting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Slet permanent
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminUserDetailPage;
